import { NextSeo } from "next-seo";
import {
  Building2,
  Flame,
  HelpCircle,
  ShieldAlert,
  Users,
  Wind,
  Zap,
} from "lucide-react";
import { ReactElement, useState } from "react";
import { Banner } from "~/components/Banner";
import FaqCategorySelector, {
  FaqCategoryOption,
} from "~/components/Faq/FaqCategorySelector";
import { MainLayout } from "~/components/layouts/MainLayout";
import { MarginLayout } from "~/components/layouts/MarginLayout";
import { NextPageWithLayout } from "~/pages/_app";
import FaqComponent from "../../components/Faq/Faq";
import faqSectionsData from "../../components/Faq/FaqContent";
import { faqPageContent as content } from "~/content/faq/content.pl";

interface FaqCategoryDefinition {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  sectionTitles: string[];
}

const faqCategoryDefinitions: FaqCategoryDefinition[] = [
  {
    key: "general",
    label: "Ogólne",
    description: "Platforma, terminy wizyt, dokumenty i przebieg zlecenia.",
    icon: HelpCircle,
    sectionTitles: ["Najczęstsze pytania ogólne"],
  },
  {
    key: "electrical",
    label: "Elektryczne",
    description:
      "Przeglądy instalacji, pomiary, rozdzielnie, fotowoltaika i magazyny energii.",
    icon: Zap,
    sectionTitles: [
      "FAQ - bezpieczeństwo elektryczne",
      "FAQ - pomiary elektryczne",
      "FAQ - przegląd instalacji odgromowej",
      "FAQ - rozdzielnia elektryczna",
      "FAQ - przegląd instalacji fotowoltaicznej",
      "FAQ - przegląd magazynu energii",
      "FAQ - modernizacja tablic rozdzielczych",
    ],
  },
  {
    key: "gas",
    label: "Gazowe",
    description:
      "Instalacje gazowe, próby szczelności, kotły i odbiory instalacji.",
    icon: Flame,
    sectionTitles: [
      "FAQ - Bezpieczeństwo Gazowe",
      "FAQ - przeglądy gazowe w przyczepach kempingowych",
      "FAQ - próba szczelności gazu",
      "FAQ - przegląd instalacji gazowej",
      "FAQ - przegląd kotła gazowego",
      "FAQ - odbiór instalacji gazowej",
    ],
  },
  {
    key: "fire-safety",
    label: "Dym i PPOŻ",
    description:
      "Czujniki dymu, systemy wykrywania pożaru i zabezpieczenia ewakuacyjne.",
    icon: ShieldAlert,
    sectionTitles: [
      "FAQ - czujniki dymu",
      "FAQ - Czujniki Dymu Połączone w Sieć",
      "FAQ - przegląd systemów wykrywania pożaru",
      "FAQ - przegląd sprzętu przeciwpożarowego",
      "FAQ - przegląd zabezpieczeń ewakuacyjnych i przeciwpożarowych",
    ],
  },
  {
    key: "chimney-hvac",
    label: "Kominy i HVAC",
    description:
      "Kominy, wentylacja, klimatyzacja i pompy ciepła w jednym miejscu.",
    icon: Wind,
    sectionTitles: [
      "FAQ - przegląd kominiarski",
      "FAQ - przegląd klimatyzacji",
      "FAQ - przegląd pompy ciepła",
      "FAQ - przegląd wentylacji",
    ],
  },
  {
    key: "building",
    label: "Budowlane",
    description: "Okresowe kontrole budynku i obowiązki wynikające z przepisów.",
    icon: Building2,
    sectionTitles: ["FAQ - przegląd budowlany"],
  },
  {
    key: "owners-managers",
    label: "Właściciele i zarządcy",
    description:
      "Zestaw pytań dopasowany do typu klienta i sposobu zarządzania nieruchomością.",
    icon: Users,
    sectionTitles: [
      "FAQ - Rozwiązania dla Właścicieli Nieruchomości na Wynajem",
      "FAQ - Rozwiązania dla Zarządców Nieruchomości",
      "FAQ - Rozwiązania dla Właścicieli Domów",
      "FAQ - Rozwiązania dla Zarządców Budynków",
      "FAQ - Rozwiązania dla Agencji Nieruchomości",
    ],
  },
];

const getQuestionIdsBySectionTitles = (sectionTitles: string[]) => {
  const sectionsSet = new Set(sectionTitles);

  return faqSectionsData
    .filter((section) => sectionsSet.has(section.title))
    .flatMap((section) => section.items.map((item) => item.id));
};

const faqCategories: FaqCategoryOption[] = faqCategoryDefinitions.map(
  ({ sectionTitles, ...category }) => ({
    ...category,
    questionIds: getQuestionIdsBySectionTitles(sectionTitles),
  })
);

const FaqPage: NextPageWithLayout = () => {
  const [activeCategoryKey, setActiveCategoryKey] = useState(
    faqCategories[0]?.key ?? ""
  );
  const activeCategory =
    faqCategories.find((category) => category.key === activeCategoryKey) ??
    faqCategories[0];

  return (
    <>
      <Banner title={content.seo.title} />
      <MarginLayout className="mx-auto flex-col sm:-mb-20">
        <FaqCategorySelector
          categories={faqCategories}
          activeCategoryKey={activeCategory.key}
          onSelect={setActiveCategoryKey}
        />
        <FaqComponent
          sections={faqSectionsData}
          selectedIds={activeCategory.questionIds}
        />
        <p className="mt-2 text-sm italic text-slate-500">{content.note}</p>
      </MarginLayout>
    </>
  );
};

FaqPage.getLayout = function GetLayout(page: ReactElement) {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
      />
      {page}
    </MainLayout>
  );
};

export default FaqPage;
